import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { AlertConfig } from "@/api/alerts-api"

import { Plus, Save, Trash2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { useUpdateAlertConfigMutation } from "../../hooks/admin-queries"

type Props = {
  config: AlertConfig
}

type RoutingRule = {
  key: string
  channels: string[]
}

const AVAILABLE_CHANNELS = ["email", "webhook"]

export function AlertsRoutingConfig({ config }: Props) {
  const { t } = useTranslation()
  const updateMutation = useUpdateAlertConfigMutation()

  const [rules, setRules] = useState<RoutingRule[]>(() =>
    Object.entries(config.routing).map(([key, channels]) => ({
      key,
      channels,
    }))
  )
  const [newRuleKey, setNewRuleKey] = useState("")

  const handleAddRule = () => {
    if (!newRuleKey.trim()) return
    if (rules.some((r) => r.key === newRuleKey)) return

    setRules([...rules, { key: newRuleKey.trim(), channels: ["email"] }])
    setNewRuleKey("")
  }

  const handleRemoveRule = (key: string) => {
    setRules(rules.filter((r) => r.key !== key))
  }

  const handleToggleChannel = (ruleKey: string, channel: string, enabled: boolean) => {
    setRules(
      rules.map((r) => {
        if (r.key !== ruleKey) return r
        const newChannels = enabled
          ? [...r.channels, channel]
          : r.channels.filter((c) => c !== channel)
        return { ...r, channels: newChannels }
      })
    )
  }

  const handleSave = async () => {
    const routing: Record<string, string[]> = {}
    for (const rule of rules) {
      if (rule.channels.length > 0) {
        routing[rule.key] = rule.channels
      }
    }
    await updateMutation.mutateAsync({ routing })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("adminDashboard.alerts.routingRules", "Routing Rules")}</CardTitle>
        <CardDescription>
          {t(
            "adminDashboard.alerts.routingDesc",
            "Configure which channels receive alerts based on alert type or severity"
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border">
          <div className="bg-muted/50 hidden grid-cols-12 gap-4 border-b px-4 py-2 text-sm font-medium lg:grid">
            <div className="col-span-5">
              {t("adminDashboard.alerts.ruleKey", "Rule (alert_type or severity:level)")}
            </div>
            <div className="col-span-5">{t("adminDashboard.alerts.channels", "Channels")}</div>
            <div className="col-span-2 text-right">{t("common.actions", "Actions")}</div>
          </div>

          {rules.length === 0 ? (
            <div className="text-muted-foreground px-4 py-8 text-center">
              {t("adminDashboard.alerts.noRules", "No routing rules configured")}
            </div>
          ) : (
            rules.map((rule) => (
              <div
                key={rule.key}
                className="admin-responsive-grid-row grid grid-cols-12 items-center gap-4 border-b px-4 py-3 last:border-b-0"
              >
                <div className="col-span-5">
                  <Badge variant={rule.key === "default" ? "default" : "outline"}>{rule.key}</Badge>
                </div>
                <div className="col-span-5 flex items-center gap-4">
                  <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
                    Channels
                  </span>
                  {AVAILABLE_CHANNELS.map((channel) => (
                    <div key={channel} className="flex items-center gap-2">
                      <Checkbox
                        checked={rule.channels.includes(channel)}
                        id={`${rule.key}-${channel}`}
                        onCheckedChange={(checked) =>
                          handleToggleChannel(rule.key, channel, !!checked)
                        }
                      />
                      <Label className="text-sm font-normal" htmlFor={`${rule.key}-${channel}`}>
                        {channel}
                      </Label>
                    </div>
                  ))}
                </div>
                <div className="col-span-2 flex justify-end">
                  <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
                    Actions
                  </span>
                  {rule.key !== "default" && (
                    <Button
                      aria-label={`Remove ${rule.key} routing rule`}
                      size="icon"
                      variant="ghost"
                      onClick={() => handleRemoveRule(rule.key)}
                    >
                      <Trash2 className="text-destructive size-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add new rule */}
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          <Input
            className="w-full sm:max-w-xs"
            placeholder="docint_quota_exceeded or severity:critical"
            value={newRuleKey}
            onChange={(e) => setNewRuleKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddRule()}
          />
          <Button size="sm" variant="outline" onClick={handleAddRule}>
            <Plus className="mr-2 size-4" />
            {t("adminDashboard.alerts.addRule", "Add Rule")}
          </Button>
        </div>

        <div className="text-muted-foreground text-sm">
          <p>
            {t(
              "adminDashboard.alerts.routingHelp",
              "Rules are matched in order: alert_type first, then severity:level, then default."
            )}
          </p>
          <p className="mt-1">
            {t(
              "adminDashboard.alerts.routingExamples",
              "Examples: docint_quota_exceeded, index_failure, severity:critical, severity:warning"
            )}
          </p>
        </div>

        <Button className="w-full" disabled={updateMutation.isPending} onClick={handleSave}>
          <Save className="mr-2 size-4" />
          {t("common.save", "Save Routing Rules")}
        </Button>
      </CardContent>
    </Card>
  )
}
